import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { Badge, Modal } from "../lib/ui.js";
import { formatDateTimeMedium, formatTime, formatWeekdayDate } from "../lib/datetime.js";

/** How the booking was made, as the client sees it: phone (the receptionist), online (the customer, on the booking page) or web (staff, in the portal). */
export function bookingSourceLabel(createdVia: string | null | undefined): string {
  return createdVia === "voice" ? "Phone" : createdVia === "web" ? "Online" : createdVia ? "Web" : "-";
}

/** First phone number on a customer row joined as `customers(name, customer_phones(e164))`. */
export function customerPhoneLabel(customer: { customer_phones?: Array<{ e164: string }> | null } | null | undefined): string {
  const e164 = customer?.customer_phones?.[0]?.e164;
  return e164 ? formatAuPhoneForDisplay(e164) : "-";
}

const STATUS_TONE: Record<string, "success" | "danger" | "neutral" | "warning"> = {
  confirmed: "success",
  cancelled: "danger",
  completed: "neutral",
  no_show: "warning",
};

interface BookingDetail {
  status: string;
  created_via: string;
  customer_name: string | null;
  booked_by: string | null;
  /** What the customer typed on the online booking page (bookings.customer_note), if anything. */
  note: string | null;
  phone: string;
  services: string;
  staff: string;
  starts_at: string | null;
  ends_at: string | null;
}

/** A call that made or changed the booking, with its curated summary (rpc_portal_booking_calls: the same fields the Calls pop-up shows). */
interface BookingCall {
  call_id: string;
  started_at: string;
  what: string;
  headline: string | null;
  details: string | null;
  action_required: string | null;
  follow_up: string | null;
}

const CALL_DID: Record<string, string> = {
  booking_confirmed: "Booked on this call",
  booking_rescheduled: "Moved on this call",
  booking_cancelled: "Cancelled on this call",
  bookings_combined: "Combined on this call",
  booking_service_added: "Service added on this call",
  booking_service_removed: "Service removed on this call",
};

/** The same details the dashboard's "Today's bookings" shows for a booking, in a pop-up, plus the summary of the call(s) behind it. A
 *  booking with several services is one visit: first start to last end, services listed in order. */
export function BookingDetailsModal({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState<BookingCall[]>([]);

  useEffect(() => {
    let cancelled = false;
    setCalls([]);
    // A booking made on the web or in the portal has no call: no rows, and no summary section.
    void supabase.rpc("rpc_portal_booking_calls", { p_booking_id: bookingId }).then(({ data }) => {
      if (!cancelled) setCalls((data ?? []) as BookingCall[]);
    });
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void supabase
      .from("bookings")
      .select(
        "status, created_via, guest_name, customer_note, customers(name, customer_phones(e164)), booking_items(starts_at, ends_at, cancelled_at, services(name), resources(name))"
      )
      .eq("id", bookingId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) {
          setDetail(null);
        } else {
          const b = data as any;
          // A cancelled booking has all its items cancelled; show them so its details stay readable.
          const all: any[] = (b.booking_items ?? []).slice().sort((x: any, y: any) => Date.parse(x.starts_at) - Date.parse(y.starts_at));
          const live = all.filter((i) => !i.cancelled_at);
          const items = live.length ? live : all;
          setDetail({
            status: b.status,
            created_via: b.created_via,
            customer_name: b.guest_name ?? b.customers?.name ?? null,
            booked_by: b.guest_name ? (b.customers?.name ?? null) : null,
            note: b.customer_note ?? null,
            phone: customerPhoneLabel(b.customers),
            services: items.map((i) => i.services?.name).filter(Boolean).join(" + ") || "-",
            staff: [...new Set(items.map((i) => i.resources?.name).filter(Boolean))].join(", ") || "-",
            starts_at: items[0]?.starts_at ?? null,
            ends_at: items[items.length - 1]?.ends_at ?? null,
          });
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  return (
    <Modal title="Booking details" onClose={onClose} width={520}>
      {loading ? (
        <p style={{ color: "var(--rc-text-muted)", margin: 0 }}>Loading...</p>
      ) : !detail ? (
        <p style={{ color: "var(--rc-text-muted)", margin: 0 }}>Booking not found.</p>
      ) : (
        <>
        <dl style={{ margin: 0, fontSize: 14, display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 10, columnGap: 12 }}>
          <Term>Time</Term>
          <dd style={ddStyle}>
            {detail.starts_at
              ? `${formatWeekdayDate(detail.starts_at)}, ${formatTime(detail.starts_at)}${detail.ends_at ? ` to ${formatTime(detail.ends_at)}` : ""}`
              : "-"}
          </dd>
          <Term>Customer</Term>
          <dd style={ddStyle}>{detail.customer_name ?? "-"}</dd>
          {detail.booked_by && (
            <>
              <Term>Booked by</Term>
              <dd style={ddStyle}>{detail.booked_by}</dd>
            </>
          )}
          <Term>Service</Term>
          <dd style={ddStyle}>{detail.services}</dd>
          <Term>With</Term>
          <dd style={ddStyle}>{detail.staff}</dd>
          <Term>Status</Term>
          <dd style={ddStyle}>
            <Badge tone={STATUS_TONE[detail.status] ?? "neutral"}>{detail.status.replace("_", " ")}</Badge>
          </dd>
          <Term>Phone</Term>
          <dd style={ddStyle}>{detail.phone}</dd>
          <Term>Source</Term>
          <dd style={ddStyle}>{bookingSourceLabel(detail.created_via)}</dd>
        </dl>
        {detail.note && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 15, margin: "0 0 8px" }}>
              Customer note
              <span style={{ fontWeight: 400, fontSize: 13, color: "var(--rc-text-muted)", marginLeft: 8 }}>Written when booking online</span>
            </h3>
            <div style={{ background: "var(--rc-surface-sunken)", border: "1px solid var(--rc-border)", borderRadius: 8, padding: 16, fontSize: 14 }}>
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{detail.note}</p>
            </div>
          </div>
        )}
        {calls.map((c) => (
          <div key={c.call_id} style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 15, margin: "0 0 8px" }}>
              Call summary
              <span style={{ fontWeight: 400, fontSize: 13, color: "var(--rc-text-muted)", marginLeft: 8 }}>
                {CALL_DID[c.what] ?? "Changed on this call"}, {formatDateTimeMedium(c.started_at)}
              </span>
            </h3>
            {c.headline ? (
              <div style={{ background: "var(--rc-surface-sunken)", border: "1px solid var(--rc-border)", borderRadius: 8, padding: 16, fontSize: 14 }}>
                <p style={{ fontWeight: 600, marginTop: 0 }}>{c.headline}</p>
                {c.details && <p>{c.details}</p>}
                {c.action_required && (
                  <p>
                    <strong>Action required:</strong> {c.action_required}
                  </p>
                )}
                {c.follow_up && (
                  <p style={{ marginBottom: 0 }}>
                    <strong>Follow-up:</strong> {c.follow_up}
                  </p>
                )}
              </div>
            ) : (
              <p style={{ color: "var(--rc-text-faint)", margin: 0 }}>No summary for this call.</p>
            )}
          </div>
        ))}
        </>
      )}
    </Modal>
  );
}

const ddStyle: React.CSSProperties = { margin: 0, color: "var(--rc-text)" };

function Term({ children }: { children: string }) {
  return <dt style={{ color: "var(--rc-text-muted)" }}>{children}</dt>;
}
